import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, Users, CheckCircle, Clock, MapPin, Camera } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { supabase } from '@/integrations/supabase/client';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

interface DashboardStats {
  totalActivations: number;
  pendingActivations: number;
  completedActivations: number;
  teamMembers: number;
  installedCount: number;
  needsPhotoCount: number;
}

interface RecentActivation {
  id: string;
  firstName: string;
  lastName: string | null;
  assignedEmail: string | null;
  assignedAt: string | null;
  status: 'pending' | 'complete';
}

const PortalDashboard = () => {
  const navigate = useNavigate();
  const { company, isOwner, hasPermission } = usePortalAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivations, setRecentActivations] = useState<RecentActivation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!company?.id) return;

      try {
        // Fetch memorials linked to this company
        const { data: memorials, error: memorialsError } = await supabase
          .from('memorials')
          .select('id, first_name, last_name, user_id, created_at')
          .eq('monument_company_id', company.id)
          .order('created_at', { ascending: false });

        if (memorialsError) {
          console.error('Error fetching memorials:', memorialsError);
          return;
        }

        // Fetch B2B tokens for these memorials to get assignment info
        const memorialIds = memorials?.map(m => m.id) || [];
        const { data: tokens, error: tokensError } = await supabase
          .from('memorial_tokens')
          .select('memorial_id, assigned_email, assigned_at, claimed')
          .eq('token_type', 'b2b')
          .in('memorial_id', memorialIds.length > 0 ? memorialIds : ['']);

        if (tokensError) {
          console.error('Error fetching tokens:', tokensError);
        }

        // Create a map of memorial_id to token info
        const tokenMap = new Map(tokens?.map(t => [t.memorial_id, t]) || []);

        // Calculate stats
        const total = memorials?.length || 0;
        const completed = memorials?.filter(m => m.user_id !== null).length || 0;
        const pending = total - completed;

        // Fetch team members count
        const { count: teamCount, error: teamError } = await supabase
          .from('monument_company_users')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', company.id);

        if (teamError) {
          console.error('Error fetching team count:', teamError);
        }

        // Fetch installation counts
        const { data: installations, error: installError } = await supabase
          .from('monument_installations')
          .select('memorial_id')
          .in('memorial_id', memorialIds.length > 0 ? memorialIds : ['']);

        if (installError) {
          console.error('Error fetching installations:', installError);
        }

        const installedMemorialIds = new Set(installations?.map(i => i.memorial_id) || []);
        const installedCount = installedMemorialIds.size;
        const needsPhotoCount = total - installedCount;

        setStats({
          totalActivations: total,
          pendingActivations: pending,
          completedActivations: completed,
          teamMembers: teamCount || 0,
          installedCount,
          needsPhotoCount,
        });

        // Format recent activations
        const recent: RecentActivation[] = (memorials || []).slice(0, 10).map(m => {
          const token = tokenMap.get(m.id);
          return {
            id: m.id,
            firstName: m.first_name,
            lastName: m.last_name,
            assignedEmail: token?.assigned_email || null,
            assignedAt: token?.assigned_at || m.created_at,
            status: m.user_id ? 'complete' : 'pending',
          };
        });

        setRecentActivations(recent);
      } catch (error) {
        console.error('Dashboard error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [company?.id]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Dashboard</h2>
        <p className="text-muted-foreground">
          Welcome back! Here's an overview of your activations.
        </p>
      </div>

      {/* Activation Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Activations</CardTitle>
            <QrCode className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalActivations || 0}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.pendingActivations || 0}</div>
            <p className="text-xs text-muted-foreground">Awaiting activation</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.completedActivations || 0}</div>
            <p className="text-xs text-muted-foreground">Activated memorials</p>
          </CardContent>
        </Card>

        {isOwner && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Team Members</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.teamMembers || 0}</div>
              <p className="text-xs text-muted-foreground">Active members</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Installation Status Cards */}
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <Card 
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => navigate('/portal/memorials?installation=installed')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Installed</CardTitle>
            <MapPin className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.installedCount || 0}</div>
            <p className="text-xs text-muted-foreground">Has installation photo</p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => navigate('/portal/memorials?installation=needs_photo')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Needs Photo</CardTitle>
            <Camera className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.needsPhotoCount || 0}</div>
            <p className="text-xs text-muted-foreground">Awaiting photo upload</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activations</CardTitle>
          <CardDescription>
            Your latest memorial activations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentActivations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No activations yet. Start by scanning a QR code.
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Client Email</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentActivations.map((activation) => (
                    <TableRow key={activation.id}>
                      <TableCell className="font-medium">
                        <div>{activation.firstName} {activation.lastName || ''}</div>
                        <div className="text-xs text-muted-foreground sm:hidden">
                          {activation.assignedEmail || '-'}
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {activation.assignedEmail || '-'}
                      </TableCell>
                      <TableCell>
                        {activation.assignedAt
                          ? new Date(activation.assignedAt).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={activation.status === 'complete' ? 'default' : 'secondary'}
                        >
                          {activation.status === 'complete' ? 'Complete' : 'Pending'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalDashboard;
